import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { SyncProvider } from './SyncProvider.js';

// ponytail: AppRoot scope — only files in this app's isolated AppRoot folder
const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';
const SCOPES = ['Files.ReadWrite.AppFolder'];

// Module-level singleton, shared by every OneDriveProvider instance.
//
// redirectUri points at a dedicated page (onedrive-redirect.html, its own Vite build entry — see
// vite.config.js), NOT this app's own root. Two independent reasons it can't be the SPA root:
//
// 1. msal-browser 5.x popup flows relay the auth response to the opener via a BroadcastChannel
//    (redirect-bridge's broadcastResponseToMainFrame(), which onedrive-redirect.html calls) rather
//    than the opener polling the popup's location — that polling approach broke once
//    login.microsoftonline.com started sending Cross-Origin-Opener-Policy headers, which sever the
//    opener/popup relationship. Without a page that calls broadcastResponseToMainFrame(), the
//    popup just sits inert (reproduced directly: it closed only via its own fallback timer, never
//    via MSAL).
// 2. This app's Vue Router runs in hash mode and would parse/rewrite the same URL fragment
//    (`#code=...&client_info=...`) as a route before that broadcast could happen (reproduced
//    directly: it became the mangled route `#/code=...&client_info=...`).
//
// No `.html` on the path: Cloudflare Workers Assets 307-redirects `/onedrive-redirect.html` to
// the extensionless `/onedrive-redirect` (its canonical "pretty URL" for that file, verified
// directly against the deployed site). MSAL's popup-completion check matches the popup's URL
// against this exact string — a mid-flight 307 inside the popup is exactly the kind of extra
// navigation step that could drop or delay the fragment, so redirectUri must be the URL Cloudflare
// actually serves with a 200, not the one that 307s.
export const msalInstance = new PublicClientApplication({
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID || 'REPLACE_WITH_AZURE_CLIENT_ID',
    redirectUri: (globalThis.location?.origin || 'https://pl.ddproxy.com') + '/onedrive-redirect',
  },
  cache: { cacheLocation: 'localStorage' },
});

export class OneDriveProvider extends SyncProvider {
  _getMsal() {
    return msalInstance;
  }

  async _getAccessToken() {
    const msal = this._getMsal();
    await msal.initialize();
    const request = { account: msal.getAllAccounts()[0], scopes: SCOPES };
    try {
      return (await msal.acquireTokenSilent(request)).accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError)
        return (await msal.acquireTokenPopup(request)).accessToken;
      throw e;
    }
  }

  async _graphRequest(
    token: string,
    path: string,
    opts: RequestInit & { headers?: Record<string, string> } = {},
  ): Promise<any> {
    const res = await fetch(`${GRAPH_BASE}${path}`, {
      ...opts,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...opts.headers,
      },
    });
    if (!res.ok) throw new Error(`Graph ${opts.method ?? 'GET'} ${path} → ${res.status}`);
    return res.json();
  }

  // Graph's approot special folder already resolves to Apps/<app registration name> for this
  // app -- no need to nest another SyncProvider.FOLDER_NAME segment inside it (that's only needed
  // for GoogleDriveProvider, which has no equivalent per-app special folder).
  _filePath(name) {
    return `/me/drive/special/approot:/${name}:`;
  }

  async _downloadFile(token, name) {
    try {
      const res = await fetch(`${GRAPH_BASE}${this._filePath(name)}/content`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 404) return null;
      if (!res.ok) throw new Error(`downloadFile ${name} → ${res.status}`);
      return res.json();
    } catch {
      return null;
    }
  }

  async _uploadFile(token, name, content) {
    const res = await fetch(`${GRAPH_BASE}${this._filePath(name)}/content`, {
      body: JSON.stringify(content),
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      method: 'PUT',
    });
    if (!res.ok) throw new Error(`uploadFile ${name} → ${res.status}`);
  }

  // ── SyncProvider contract ──────────────────────────────────────────────────

  // opts.overrideInteractionInProgress: MSAL tracks interactive-request state in browser storage
  // and throws BrowserAuthError('interaction_in_progress') if it thinks a prior popup flow never
  // finished cleanly. Per MSAL's own guidance this must only be set from an explicit user retry
  // action (never auto-retried) — see SyncSettings.vue's connect-error toast.
  override async signIn(opts: { overrideInteractionInProgress?: boolean } = {}) {
    const msal = this._getMsal();
    await msal.initialize();
    await msal.loginPopup({ scopes: SCOPES, ...opts });
  }

  override async signOut() {
    const msal = this._getMsal();
    await msal.initialize();
    const accounts = msal.getAllAccounts();
    if (accounts[0]) await msal.logoutPopup({ account: accounts[0] });
  }

  override async uploadData(name, data) {
    const token = await this._getAccessToken();
    return this._uploadFile(token, name, data);
  }

  override async downloadData(name) {
    const token = await this._getAccessToken();
    return this._downloadFile(token, name);
  }

  async _uploadBlobFile(token: string, name: string, blob: Blob, mimeType: string) {
    const res = await fetch(`${GRAPH_BASE}${this._filePath(name)}/content`, {
      body: blob,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': mimeType },
      method: 'PUT',
    });
    if (!res.ok) throw new Error(`uploadBlob ${name} → ${res.status}`);
  }

  // ── OneDrive-specific extension ────────────────────────────────────────────

  async uploadBlob(name: string, blob: Blob, mimeType: string) {
    const token = await this._getAccessToken();
    return this._uploadBlobFile(token, name, blob, mimeType);
  }

  async syncBundle({ bundleId, bundleJson }) {
    const token = await this._getAccessToken();
    await this._uploadFile(token, `bundle-${bundleId}.json`, bundleJson);

    const remote = await this._downloadFile(token, SyncProvider.MANIFEST_NAME);
    const now = new Date().toISOString();
    const local = { bundles: [...(remote?.bundles ?? []), bundleId], updatedAt: now };
    const winner = OneDriveProvider.resolveManifest(local, remote);
    if (!winner.bundles.includes(bundleId)) winner.bundles.push(bundleId);
    winner.updatedAt = now;
    await this._uploadFile(token, SyncProvider.MANIFEST_NAME, winner);
    return now;
  }
}

// ── Module singleton ─────────────────────────────────────────────────────────
// One instance per page lifecycle, matching the single MSAL instance above.
// Exported as the instance rather than a factory: destructuring methods off a
// class loses `this`, so callers use oneDrive.method(...) directly.
export const oneDrive = new OneDriveProvider();

export const oneDriveSignIn = () => oneDrive.signIn();
export const oneDriveSignOut = () => oneDrive.signOut();
export const resolveOneDriveManifest = (local, remote) => SyncProvider.resolveManifest(local, remote);
